import { useState } from 'react'
import { Modal, Button, Checkbox, Tooltip } from '@mantine/core'
import classNames from 'classnames'
import dayjs from '@/lib/dayjs'
import Box from '@/components/ui/Box'
import useServiceInterventions from '@/hooks/useServiceInterventions'
import useProfiles from '@/hooks/useProfiles'

// Conflits entre interventions (symétriques)
// id=1 (Réglage-Étanchéité-Démagnétisation) est inclus dans toutes les autres
const INTERVENTION_CONFLICTS = {
    1:  [2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22],
    2:  [1,3,5,6,7,8,9,10,16,17,18,20,22],   // Réparation Mouvement 3A : inclut aiguilles 3A, exclut GMT, exclut remplacement/révision mouvement
    3:  [1,2,5,6,7,8,9,16,17,18,20,22],      // Remplacement Aiguilles 3A : déjà inclus dans réparation/remplacement mouvement 3A, exclut GMT
    4:  [1,6,7,8,9,16,17,18,20,22],          // Remplacement verre 3A : exclut GMT
    5:  [1,2,3,6,7,8,9,10,16,17,18,20,22],   // Remplacement mouvement 3A : inclut aiguilles 3A, exclut GMT, exclut réparation/révision mouvement
    6:  [1,2,3,4,5,7,9,10,13,14,15,19,21],   // Réparation Mouvement GMT : inclut aiguilles GMT, exclut 3A, exclut remplacement/révision mouvement
    7:  [1,2,3,4,5,6,9,13,14,15,19,21],      // Remplacement aiguilles GMT : déjà inclus dans réparation/remplacement mouvement GMT, exclut 3A
    8:  [1,2,3,4,5,13,14,15,19,21],          // Remplacement verre GMT : exclut 3A
    9:  [1,2,3,4,5,6,7,10,13,14,15,19,21],   // Remplacement mouvement GMT : inclut aiguilles GMT, exclut 3A, exclut réparation/révision mouvement
    10: [1,2,5,6,9,12],                      // Révision mouvement : exclut réparation/remplacement mouvement, déjà inclus dans ravivage+révision
    11: [1,12],                              // Ravivage Carrure/Bracelet : déjà inclus dans ravivage+révision
    12: [1,2,5,6,9,10,11],                   // Ravivage+révision mouvement : inclut révision + ravivage, exclut réparation/remplacement mouvement
    13: [1,6,7,8,9,16,17,18,20,22],          // Remplacement Insert 3A : exclut GMT
    14: [1,6,7,8,9,16,17,18,20,22],          // Remplacement Couronne 3A : exclut GMT
    15: [1,6,7,8,9,16,17,18,20,21,22],       // Remplacement Carrure 3A : exclut GMT, exclut réparation carrure 3A
    16: [1,2,3,4,5,13,14,15,19,22],          // Remplacement Insert GMT : exclut 3A
    17: [1,2,3,4,5,13,14,15,19,22],          // Remplacement Couronne GMT : exclut 3A
    18: [1,2,3,4,5,13,14,15,19,21,22],       // Remplacement Carrure GMT : exclut 3A, exclut réparation carrure GMT
    19: [1,6,7,8,9,16,17,18,20,22],          // Remplacement cadran 3A : exclut GMT
    20: [1,2,3,4,5,13,14,15,19,22],          // Remplacement cadran GMT : exclut 3A
    21: [1,6,7,8,9,15,16,17,18,20,22],       // Réparation Carrure 3A : exclut GMT, exclut remplacement carrure 3A
    22: [1,2,3,4,5,13,14,15,18,19,21],       // Réparation Carrure GMT : exclut 3A, exclut remplacement carrure GMT
}

export default function ServiceCaseInterventions({
    serviceCase,
    updateServiceCase,
}) {
    const [opened, setOpened] = useState(false)
    const [selectedIds, setSelectedIds] = useState([])
    const [loading, setLoading] = useState(false)

    const { interventionGroups, interventions } = useServiceInterventions()
    const { profilesMap } = useProfiles()

    // Active (non-canceled) interventions from the service case
    const activeInterventions = (serviceCase.interventions || []).filter(
        (i) => !i.canceled_at
    )
    const activeInterventionIds = activeInterventions.map(
        (i) => i.service_intervention_id
    )

    const handleOpen = () => {
        setSelectedIds([...activeInterventionIds])
        setOpened(true)
    }

    const isConflicted = (id) =>
        selectedIds.some((selectedId) =>
            INTERVENTION_CONFLICTS[selectedId]?.includes(id)
        )

    const toggle = (id) => {
        if (isConflicted(id)) return
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        )
    }

    const handleSave = async () => {
        setLoading(true)
        await updateServiceCase({ interventions: selectedIds })
        setLoading(false)
        setOpened(false)
    }

    return (
        <>
            <Box
                title="Interventions réalisées"
                globalAction={true}
                action={handleOpen}
                actionLabel="Modifier les interventions"
            >
                {activeInterventions.length ? (
                    <div className="flex flex-col gap-1.5">
                        {activeInterventions.map((sci) => {
                            const intervention = interventions.find(
                                (i) => i.id === sci.service_intervention_id
                            )
                            if (!intervention) return null
                            return (
                                <div
                                    key={sci.id}
                                    className="flex items-center gap-2 text-sm"
                                >
                                    <span className="text-gray-400">
                                        {intervention.group_name}
                                    </span>
                                    <span className="text-gray-300">/</span>
                                    <span>{intervention.name}</span>
                                    <span className="text-gray-300 text-xs ml-auto flex items-center gap-1.5">
                                        {sci.created_by_profile_id &&
                                            profilesMap[sci.created_by_profile_id] && (
                                                <span>
                                                    {profilesMap[sci.created_by_profile_id].name}
                                                </span>
                                            )}
                                        {sci.created_at && (
                                            <span>
                                                {dayjs(sci.created_at).format('D MMM YYYY')}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <span className="text-gray-500">
                        Aucune interentions réalisées
                    </span>
                )}
            </Box>

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title="Interventions réalisées"
                centered
                keepMounted={false}
            >
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 max-h-[500px] overflow-y-auto">
                        {interventionGroups.map((group) => (
                            <div key={group.id}>
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                                    {group.name}
                                </span>
                                <div className="flex flex-col gap-1 mt-1.5">
                                    {group.service_interventions.slice().sort((a, b) => a.name.localeCompare(b.name, 'fr')).map(
                                        (intervention) => {
                                            const conflicted = isConflicted(intervention.id)
                                            const selected = selectedIds.includes(intervention.id)
                                            return (
                                                <Tooltip
                                                    key={intervention.id}
                                                    label="Incompatible avec une intervention déjà sélectionnée"
                                                    disabled={!conflicted}
                                                    position="top"
                                                >
                                                    <div
                                                        className={classNames(
                                                            'border rounded-lg p-3 transition-colors flex items-center gap-3',
                                                            {
                                                                'cursor-pointer': !conflicted,
                                                                'cursor-not-allowed opacity-40': conflicted,
                                                                'border-blue-500 bg-blue-50': selected,
                                                                'border-gray-200 hover:bg-gray-50': !selected && !conflicted,
                                                                'border-gray-200': !selected && conflicted,
                                                            }
                                                        )}
                                                        onClick={() => toggle(intervention.id)}
                                                    >
                                                        <Checkbox
                                                            checked={selected}
                                                            disabled={conflicted}
                                                            onChange={() => {}}
                                                            readOnly
                                                        />
                                                        <span className="text-sm">
                                                            {intervention.name}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            )
                                        }
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <Button onClick={handleSave} fullWidth loading={loading}>
                        Enregistrer
                    </Button>

                    <span className="text-xs text-red-500 text-center">
                        Utilisé pour le récapitulatif d'intervention.
                        <br />À n'utiliser que par les horlogers.
                    </span>
                </div>
            </Modal>
        </>
    )
}
